// How the sales pipeline's vocabulary looks and reads, in one place: the order of the stages, the colour of each pill,
// and small components that draw a stage, a priority, a channel or a lost reason in the console's words. The words are
// in `packages/i18n/src/locales/en/hq.ts` under `leads`; nothing here spells a label.

import type { LeadActivityKind, LeadChannel, LeadLostReason, LeadPriority, LeadStage } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, type BadgeTone } from '@clavian/ui-web';
import type { ReactNode } from 'react';

/** Every stage, in the order a lead moves through them, then the three that take it out of play. */
export const LEAD_STAGE_ORDER: readonly LeadStage[] = ['new', 'contacted', 'replied', 'demo', 'proposal', 'won', 'lost', 'parked'];
/** The stages a lead is still being worked in: the board's columns. */
export const OPEN_STAGES: readonly LeadStage[] = ['new', 'contacted', 'replied', 'demo', 'proposal'];
/** The stages out of play: the board's collapsed strip. */
export const CLOSED_STAGES: readonly LeadStage[] = ['won', 'lost', 'parked'];
export const LEAD_PRIORITIES: readonly LeadPriority[] = ['a', 'b', 'c'];
export const LEAD_LOST_REASON_ORDER: readonly LeadLostReason[] = ['price', 'happy-with-current-software', 'closed-down', 'no-reply', 'not-a-fit', 'other'];
export const LEAD_MESSAGE_CHANNEL_ORDER = ['whatsapp', 'instagram', 'email'] as const;

export const STAGE_TONE: Readonly<Record<LeadStage, BadgeTone>> = {
  new: 'neutral',
  contacted: 'info',
  replied: 'accent',
  demo: 'accent',
  proposal: 'session',
  won: 'ok',
  lost: 'danger',
  parked: 'warn',
};

export const PRIORITY_TONE: Readonly<Record<LeadPriority, BadgeTone>> = { a: 'accent', b: 'info', c: 'neutral' };

/** The copy key of each word, so a caller can pass it to `t` as it is. */
export const stageKey = (stage: LeadStage) => `leads.stage.${stage}` as const;
export const channelKey = (channel: LeadChannel) => `leads.channel.${channel}` as const;
export const kindKey = (kind: LeadActivityKind) => `leads.kind.${kind}` as const;
export const lostReasonKey = (reason: LeadLostReason) => `leads.lostReason.${reason}` as const;

export function LeadStagePill({ stage }: { readonly stage: LeadStage }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Badge tone={STAGE_TONE[stage]} dot>
      {t(stageKey(stage))}
    </Badge>
  );
}

export function LeadPriorityPill({ priority }: { readonly priority: LeadPriority }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Badge tone={PRIORITY_TONE[priority]} title={t(`leads.priority.label_${priority}`)} aria-label={t(`leads.priority.label_${priority}`)}>
      {t(`leads.priority.${priority}`)}
    </Badge>
  );
}

/** A Google rating in tenths as a person reads it: `46` → `4.6`. */
export function ratingText(tenths: number): string {
  return (tenths / 10).toFixed(1);
}
